import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Bar, BarChart, CartesianGrid, Cell, XAxis, YAxis } from "recharts"

import { ChartContainer, type ChartConfig } from "@/shared/components/ui/chart"
import { getMonthlyVolumes } from "@/shared/api/statistics"

type VolumeDatum = {
  month: string
  monthKey: string
  runs: number
  isCurrent: boolean
}

const MONTH_KEYS = [
  "jan",
  "feb",
  "mar",
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec",
] as const

type YearMonth = { year: number; month: number }

const parseMonthString = (value: string): YearMonth | null => {
  const [year, month] = value.split("-").map(Number)
  if (!year || !month) return null

  return { year, month }
}

const shiftMonths = ({ year, month }: YearMonth, delta: number): YearMonth => {
  const totalMonths = year * 12 + (month - 1) + delta
  const nextYear = Math.floor(totalMonths / 12)
  const nextMonth = (totalMonths % 12) + 1
  return { year: nextYear, month: nextMonth }
}

const formatMonth = ({ year, month }: YearMonth): string =>
  `${year}-${String(month).padStart(2, "0")}`

const createTwelveMonthWindow = (anchor: YearMonth): Omit<VolumeDatum, "runs">[] =>
  Array.from({ length: 12 }, (_, index) => {
    const delta = index - 11
    const current = shiftMonths(anchor, delta)
    const month = formatMonth(current)
    const monthKey = MONTH_KEYS[current.month - 1] ?? MONTH_KEYS[0]

    return {
      month,
      monthKey,
      isCurrent: index === 11,
    }
  })

export function CalculationVolumeChart() {
  const { t } = useTranslation()
  const initialWindow = useMemo(
    () =>
      createTwelveMonthWindow({
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
      }).map((item) => ({
        ...item,
        runs: 0,
      })),
    [],
  )

  const [chartState, setChartState] = useState<{
    data: VolumeDatum[]
    hasData: boolean
  }>(() => ({
    data: initialWindow,
    hasData: false,
  }))

  useEffect(() => {
    const fetchData = async () => {
      try {
        const volumes = await getMonthlyVolumes()

        if (volumes && volumes.length > 0) {
          const sortedVolumes = [...volumes].sort((a, b) => a.month.localeCompare(b.month))
          const latest = sortedVolumes[sortedVolumes.length - 1]
          const anchor = parseMonthString(latest.month) ?? {
            year: new Date().getFullYear(),
            month: new Date().getMonth() + 1,
          }

          const actualByMonth = new Map(volumes.map((item) => [item.month, item]))
          const window = createTwelveMonthWindow(anchor).map((item) => {
            const actual = actualByMonth.get(item.month)

            return {
              month: item.month,
              monthKey: actual?.monthKey ?? item.monthKey,
              runs: actual?.runs ?? 0,
              isCurrent: item.isCurrent,
            }
          })

          const hasData = window.some((entry) => entry.runs > 0)

          setChartState({
            data: window,
            hasData,
          })
        } else {
          setChartState((previous) => ({
            ...previous,
            hasData: false,
          }))
        }
      } catch (error) {
        console.error("Error fetching monthly volumes:", error)
        // Keep fallback data on error
      }
    }

    fetchData()
  }, [])

  const { data: chartData, hasData } = chartState
  const maxRuns = chartData.reduce((max, entry) => Math.max(max, entry.runs), 0)
  const yDomain = [0, maxRuns === 0 ? 10 : Math.ceil(maxRuns * 1.1)] as [
    number | "auto" | "dataMin" | "dataMax",
    number | "auto" | "dataMin" | "dataMax",
  ]

  const config: ChartConfig = {
    runs: {
      label: t("statistics.volume.series.runs"),
      color: "hsl(var(--chart-1))",
    },
  }

  const localizedData = hasData
    ? chartData.map((entry) => ({
        ...entry,
        month: t(`common.months.short.${entry.monthKey}`),
      }))
    : []

  return (
    <ChartContainer
      className="h-[220px] w-full sm:h-[250px]"
      config={config}
      empty={hasData ? undefined : t("statistics.noData")}
    >
      {hasData && (
        <BarChart data={localizedData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient
              id="brand-gradient"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="0%"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0%" stopColor="#ff8308" />
              <stop offset="50%" stopColor="#ff5043" />
              <stop offset="100%" stopColor="#392bd5" />
            </linearGradient>
            <pattern id="gradient-image" patternUnits="userSpaceOnUse" width="800" height="300">
              <image
                href="https://cdn.prod.website-files.com/689af392aa8c5cd6612c9ef7/689af392aa8c5cd6612c9f3f_gradient-map-2.webp"
                width="800"
                height="300"
                preserveAspectRatio="xMidYMid slice"
              />
              <rect width="800" height="300" fill="url(#brand-gradient)" opacity="0.7" />
            </pattern>
          </defs>
          <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
          <XAxis
            axisLine={false}
            dataKey="month"
            stroke="hsl(var(--muted-foreground))"
            tickLine={false}
            tickMargin={10}
          />
          <YAxis
            axisLine={false}
            domain={yDomain}
            stroke="hsl(var(--muted-foreground))"
            tickFormatter={(value) => (value === yDomain[1] ? "" : `${value}`)}
            tickLine={false}
            tickMargin={8}
          />
          <Bar dataKey="runs" radius={[6, 6, 0, 0]} fill="url(#gradient-image)">
            {localizedData.map((entry) => (
              <Cell
                key={entry.month}
                fill={entry.isCurrent ? "hsl(var(--muted-foreground))" : "url(#gradient-image)"}
              />
            ))}
          </Bar>
        </BarChart>
      )}
    </ChartContainer>
  )
}
